import React, { useCallback, ChangeEvent, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { QRCodeSVG } from "qrcode.react";
import {
  Box,
  Button,
  Code,
  Heading,
  Spinner,
  Stack,
  Switch,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { WarningTwoIcon } from "@chakra-ui/icons";

import { useMutation, useQuery } from "@apollo/client";
import {
  InitializeUserOtpMutation,
  InitializeUserOtpMutation_initializeUserOtp,
  RemoveUserOtpMutation,
  RemoveUserOtpMutation_removeUserOtp,
  UserOtp,
  UserOtp_userOtp,
} from "graphql/generated";
import { USER_OTP_QUERY } from "graphql/queries";
import { INITIALIZE_USER_OTP_MUTATION, REMOVE_USER_OTP_MUTATION } from "graphql/mutations";

import { useGlobalState } from "common/global-state";
import { useToasts } from "common/toasts";

import { Layout } from "components/layout";


const UserSettings = () => {
  const intl = useIntl();
  const { user } = useGlobalState();
  const { pushErrorToast, pushSuccessToast } = useToasts();

  const queryResult = useQuery<UserOtp>(USER_OTP_QUERY, {
    skip: !user,
    fetchPolicy: "cache-and-network",
  });
  const cardBg = useColorModeValue("enposol.100", "enposol.900");
  const secretBg = useColorModeValue("black", "white");
  const secretColor = useColorModeValue("white", "black");
  const warningColor = useColorModeValue("red.600", "red.300");
  const [otpState, setOtpState] = useState<UserOtp_userOtp | InitializeUserOtpMutation_initializeUserOtp | RemoveUserOtpMutation_removeUserOtp | null>(null);

  const [initializeOtp, { loading: initializeLoading }] = useMutation<InitializeUserOtpMutation>(
    INITIALIZE_USER_OTP_MUTATION,
    {
      onCompleted: (response) => {
        if (response.initializeUserOtp) {
          setOtpState(response.initializeUserOtp);
          pushSuccessToast(intl.formatMessage({
            id: "user_settings__otp_initialized",
            defaultMessage: "Two-factor authentication enabled. Scan the QR code to finish setup.",
          }));
        }
      },
      onError: (error) => {
        pushErrorToast(error.message);
      },
    },
  );

  const [removeOtp, { loading: removeLoading }] = useMutation<RemoveUserOtpMutation>(
    REMOVE_USER_OTP_MUTATION,
    {
      onCompleted: (response) => {
        if (response.removeUserOtp) {
          setOtpState(response.removeUserOtp);
          pushSuccessToast(intl.formatMessage({
            id: "user_settings__otp_removed",
            defaultMessage: "Two-factor authentication has been removed.",
          }));
        }
      },
      onError: (error) => {
        pushErrorToast(error.message);
      },
    },
  );

  useEffect(() => {
    if (queryResult.data?.userOtp) {
      setOtpState((current) => {
        if (current?.secret) {
          return current;
        }

        return queryResult.data?.userOtp ?? current;
      });
    }
  }, [queryResult.data]);

  const regenerateSecret = useCallback(() => {
    initializeOtp();
  }, [initializeOtp]);

  const toggleTwoFactorAuth = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      initializeOtp();
    } else {
      removeOtp();
    }
  }, []);

  if (!user) {
    return <Navigate to="/" replace={true} />;
  }

  const { loading } = queryResult;
  const otpData = otpState ?? queryResult.data?.userOtp ?? null;
  const enabled = otpData?.enabled ?? false;
  const secret = otpData?.secret ?? undefined;
  const provisioningUri = otpData?.provisioningUri ?? undefined;


  return (
    <Layout>
      <VStack align="start" spacing={6} mt={4} maxW={"xl"}>
        <Heading size="lg">
          {intl.formatMessage({
            id: "user_settings__title",
            defaultMessage: "User security settings",
          })}
        </Heading>
        <VStack align="start" spacing={4}>
          <Stack direction="row" align="center" spacing={3}>
            {(loading || initializeLoading || removeLoading) ? (
              <Spinner size="md" />
            ) : (
              <Switch
                isChecked={enabled}
                isDisabled={loading}
                onChange={toggleTwoFactorAuth}
              />
            )}
            <Text fontWeight="bold">
              {intl.formatMessage({
                id: "user_settings__otp_2fa_auth",
                defaultMessage: "Two-Factor Authentication",
              })}
            </Text>
            {enabled && !loading && (
              <Button onClick={regenerateSecret} isLoading={initializeLoading} size="xs" colorScheme="yellow">
                {intl.formatMessage({
                  id: "user_settings__otp_enable_button",
                  defaultMessage: "Generate new secret",
                })}
              </Button>
            )}
          </Stack>
          {enabled && secret && provisioningUri && (
            <Stack direction="row" p={8} spacing={8} align="start" bg={cardBg}>
              <Box>
                <QRCodeSVG value={provisioningUri} size={150} includeMargin={true}  />
              </Box>
              <VStack align="start" spacing={3}>
                <Text fontWeight="bold">
                  {intl.formatMessage({
                    id: "user_settings__otp_description",
                    defaultMessage: "Now scan this QR code with your authenticator application to finish adding two-factor authentication to your account.",
                  })}
                </Text>
                <Text>
                  {intl.formatMessage({
                    id: "user_settings__otp_manual_hint",
                    defaultMessage: "If you can not scan the QR code, enter the secret to the authenticator app manually:",
                  })}
                </Text>
                <Code fontSize="lg" bg={secretBg} color={secretColor}>
                  {secret}
                </Code>
                <Stack direction="row" color={warningColor} align="baseline">
                  <WarningTwoIcon/>
                  <Text fontWeight="bold" fontSize="sm">
                    {intl.formatMessage({
                      id: "user_settings__otp_warning",
                      defaultMessage: "Store this secret safe. Anyone with access to it can generate authentication codes for your account.",
                    })}
                  </Text>
                </Stack>
              </VStack>
            </Stack>
          )}
        </VStack>
      </VStack>
    </Layout>
  );
};

export default UserSettings;
